import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  LayoutDashboard,
  ScrollText,
  GanttChartSquare,
  Wallet,
  Workflow,
  GraduationCap,
  Sparkles,
  Zap,
  Coins,
  LogIn,
  LogOut,
  UserCog,
  ShieldCheck,
} from "lucide-react";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import PricingDialog from "./PricingDialog";
import AuthDialog from "./AuthDialog";
import { useCredits } from "@/lib/CreditsContext";
import { useAuth } from "@/lib/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/contract", label: "Contract AI", icon: ScrollText, id: "contract-ai" },
  { to: "/project", label: "Project AI", icon: GanttChartSquare, id: "project-ai" },
  { to: "/finance", label: "Finance AI", icon: Wallet, id: "finance-ai" },
  { to: "/automation", label: "Automation", icon: Workflow, id: "automation" },
  { to: "/training", label: "Training", icon: GraduationCap, id: "training" },
];

export default function Layout() {
  const [pricingOpen, setPricingOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  return (
    <div className="App min-h-screen bg-[#020617] text-slate-50 terminal-grain">
      <TopNav
        onUpgrade={() => setPricingOpen(true)}
        onSignIn={() => setAuthOpen(true)}
      />
      <main className="relative z-10 mx-auto max-w-[1600px] px-4 sm:px-6 py-6">
        <Outlet />
      </main>
      <footer className="relative z-10 border-t border-slate-800/80 mt-12 py-5 px-6 flex flex-col md:flex-row gap-2 items-center justify-between text-xs text-slate-500">
        <div className="font-mono">
          3C AI · Engineering Intelligence Platform · v0.1
        </div>
        <div className="flex items-center gap-4">
          <span>FIDIC Red · Yellow · Silver · Green</span>
          <span className="text-slate-700">|</span>
          <span>Powered by Claude Sonnet 4.5</span>
          <span className="text-slate-700">|</span>
          <button
            onClick={() => setPricingOpen(true)}
            className="hover:text-cyan-400 transition-colors"
            data-testid="footer-pricing-link"
          >
            Pricing
          </button>
        </div>
      </footer>
      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}

function TopNav({ onUpgrade, onSignIn }) {
  const { balance } = useCredits();
  const { user, logout } = useAuth();
  const pct = Math.min(100, (balance / 40) * 100);
  const plan = balance >= 40 ? "Pro" : balance >= 15 ? "Growth" : balance >= 5 ? "Starter" : "Free";
  return (
    <header
      className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#020617]/95 backdrop-blur"
      data-testid="top-nav"
    >
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 h-16 flex items-center gap-6">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-2.5 group"
          data-testid="logo-home-link"
        >
          <div className="h-8 w-8 grid place-items-center bg-cyan-500 text-slate-950 rounded-sm">
            <Sparkles className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-bold text-lg tracking-tight">
              3C<span className="text-cyan-400"> AI</span>
            </span>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              Engineering Intelligence
            </span>
          </div>
        </Link>

        {/* Nav links */}
        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                [
                  "px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-sm transition-colors",
                  isActive
                    ? "text-cyan-400 bg-slate-900/70 border border-slate-800"
                    : "text-slate-400 hover:text-white hover:bg-slate-900/50 border border-transparent",
                ].join(" ")
              }
            >
              <item.icon className="h-3.5 w-3.5" strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right panel */}
        <div className="ml-auto flex items-center gap-4">
          <button
            onClick={onUpgrade}
            className="hidden md:flex flex-col items-end group"
            data-testid="plan-pill"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 group-hover:text-cyan-400 transition-colors flex items-center gap-1.5">
              <Coins className="h-3 w-3" />
              {plan} Plan
            </div>
            <div className="flex items-center gap-2">
              <span
                className="text-sm font-mono text-slate-200"
                data-testid="credit-balance"
              >
                {balance}
                <span className="text-slate-500"> credits</span>
              </span>
              <Progress
                value={pct}
                className="h-1 w-20 bg-slate-800 [&>div]:bg-cyan-500"
              />
            </div>
          </button>
          <Button
            size="sm"
            onClick={onUpgrade}
            data-testid="upgrade-btn"
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm gap-1.5"
          >
            <Zap className="h-3.5 w-3.5" /> Upgrade
          </Button>
          {user ? (
            <div className="hidden md:flex items-center gap-2" data-testid="user-menu">
              <Link
                to="/account"
                className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-cyan-400 px-2 py-1 rounded-sm border border-slate-800 flex items-center gap-1"
                data-testid="nav-account"
                title="My account"
              >
                <UserCog className="h-3 w-3" /> Account
              </Link>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="text-[11px] font-mono uppercase tracking-[0.18em] text-amber-300 hover:text-amber-200 px-2 py-1 rounded-sm border border-amber-500/40 bg-amber-500/5 flex items-center gap-1"
                  data-testid="nav-admin"
                  title="Admin"
                >
                  <ShieldCheck className="h-3 w-3" /> Admin
                </Link>
              )}
              <div className="text-right leading-tight">
                <div
                  className="text-[11px] font-mono text-slate-100 max-w-[160px] truncate"
                  data-testid="user-email"
                  title={user.email}
                >
                  {user.email}
                </div>
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500">
                  Signed in
                </div>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={logout}
                data-testid="logout-btn"
                title="Sign out"
                className="h-9 w-9 rounded-sm border border-slate-800 hover:bg-slate-900 text-slate-400 hover:text-rose-300"
              >
                <LogOut className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onSignIn}
              data-testid="signin-btn"
              className="bg-transparent border-slate-700 hover:bg-slate-900 text-slate-100 font-semibold rounded-sm gap-1.5"
            >
              <LogIn className="h-3.5 w-3.5" /> Sign in
            </Button>
          )}
        </div>
      </div>

      {/* mobile nav */}
      <nav className="lg:hidden flex overflow-x-auto scroll-clean border-t border-slate-800/80 px-2">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`nav-mobile-${item.id}`}
            className={({ isActive }) =>
              [
                "shrink-0 px-3 py-2 text-xs font-medium flex items-center gap-1.5",
                isActive
                  ? "text-cyan-400 border-b-2 border-cyan-500"
                  : "text-slate-400 border-b-2 border-transparent",
              ].join(" ")
            }
          >
            <item.icon className="h-3.5 w-3.5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}
